import { Box, Paper, Skeleton, Tooltip, Typography } from '@mui/material';
import { getGuardContacts } from 'api/canguard/getGuardContacts';
import {
	Autocomplete,
	Grid,
	DatePicker,
	TextField,
	AdapterDateFns,
	LocalizationProvider,
	Button,
} from 'components/mui';
import { useGuardRoleContext } from 'containers/pages/CANguard/context/GuardRoleProvider';
import { GuardTabs } from 'pages/CANguard/CANguard';
import React, { useState, useEffect } from 'react';
import { useNotificationProvider } from 'hooks';

interface AdminTabProps {
	goToTab: (tab: GuardTabs) => void;
}

interface GuardContact {
	contactID: number;
	name: string;
}

const AdminTab: React.FC<AdminTabProps> = (props) => {
	const { isAdmin, setGuardSelectorOptions, guardSelectorOptions } =
		useGuardRoleContext();
	const { notifyError } = useNotificationProvider();
	const [guards, setGuards] = useState<GuardContact[]>([]);
	const [loadingGuards, setLoadingGuards] = useState<boolean>(false);
	const [selectedGuard, setSelectedGuard] = useState<GuardContact | null>(
		null,
	);
	const [selectedGuardError, setSelectedGuardError] = useState<string>('');
	const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
	const [selectedDateError, setSelectedDateError] = useState<string>('');

	const handleGetScheduleClick = () => {
		if (!selectedGuard) {
			setSelectedGuardError('Please select a guard');
		} else {
			setSelectedGuardError('');
		}
		if (!selectedDate) {
			setSelectedDateError('Please select a date');
		} else {
			setSelectedDateError('');
		}
		if (selectedGuard && selectedDate) {
			setGuardSelectorOptions({
				selectedGuardID: selectedGuard.contactID,
				selectedGuardName: selectedGuard.name,
				selectedDate,
			});
			props.goToTab(GuardTabs.Patrols);
		}
	};

	async function fetchGuards() {
		try {
			setLoadingGuards(true);
			const response = await getGuardContacts();
			const transformedGuards: GuardContact[] = response.Data.map(
				(guard: any) => ({
					contactID: parseInt(guard.Id, 10),
					name: guard.FullName,
				}),
			);
			setGuards(transformedGuards);
		} catch (error: any) {
			console.error('[AdminTab/fetchGuards] Error fetching guard contacts:', error);
			notifyError('Failed to load guard contacts. Please try again.');
			setGuards([]);
		} finally {
			setLoadingGuards(false);
		}
	}

	useEffect(() => {
		fetchGuards();
	}, []);

	if (!isAdmin) {
		return null;
	}
	const handleClearSelectionClick = () => {
		setSelectedGuard(null);
		setSelectedDate(new Date());
		setGuardSelectorOptions({
			selectedGuardID: null,
			selectedGuardName: '',
			selectedDate: new Date(),
		});
	};
	const isSelected =
		selectedGuard?.contactID === guardSelectorOptions.selectedGuardID &&
		selectedDate === guardSelectorOptions.selectedDate;

	const isFormInvalid = !selectedGuard || !selectedDate;

	const getScheduleButtonTooltip = (): string => {
		if (!selectedGuard && !selectedDate) return 'Please select a guard and a date';
		if (!selectedGuard) return 'Please select a guard';
		if (!selectedDate) return 'Please select a date';
		return '';
	};

	return (
		<Paper
			elevation={1}
			sx={{
				mx: 'auto',
				mt: 2,
				width: '100%',
				maxWidth: '500px',
			}}
		>
			<Grid
				container
				sx={{
					display: 'flex',
					justifyContent: 'center',
				}}
			>
				<Grid
					sx={{
						width: '100%',
						maxWidth: '500px',
						p: 2,
						display: 'flex',
						flexDirection: 'column',
						gap: 2,
					}}
				>
					<Box>
						<Typography variant="h5" textAlign={'center'}>
							Admin Panel
						</Typography>
					</Box>
					{loadingGuards ? (
						<>
							<Skeleton variant="rounded" height={56} />
							<Skeleton variant="rounded" height={56} />
							<Skeleton variant="rounded" height={36} width={120} sx={{ mx: 'auto' }} />
						</>
					) : (
						<>
							<Autocomplete
								options={guards}
								loading={loadingGuards}
								loadingText="Loading Guards..."
								getOptionLabel={(option) => option.name}
								isOptionEqualToValue={(option, value) => {
									return option.contactID === value.contactID;
								}}
								value={selectedGuard}
								onChange={(event, newValue) => {
									setSelectedGuardError('');
									setSelectedGuard(newValue);
								}}
								filterSelectedOptions
								renderInput={(params) => (
									<TextField
										{...params}
										variant="outlined"
										label="Guard Contacts"
										placeholder="Guard Contacts"
										error={!!selectedGuardError}
										helperText={selectedGuardError || ' '}
									/>
								)}
							/>
							<LocalizationProvider dateAdapter={AdapterDateFns}>
								<DatePicker
									label="Date"
									value={selectedDate}
									onChange={(newValue) => {
										setSelectedDateError('');
										setSelectedDate(newValue);
									}}
									views={['year', 'month', 'day']}
									openTo="day"
									renderInput={(params) => (
										<TextField
											{...params}
											error={!!selectedDateError}
											helperText={selectedDateError || ' '}
										/>
									)}
								/>
							</LocalizationProvider>
							<Box
								sx={{
									width: '100%',
									display: 'flex',
									justifyContent: 'center',
									gap: 2,
								}}
							>
								{isSelected ? (
									<Button
										variant="outlined"
										onClick={handleClearSelectionClick}
										color="error"
									>
										Clear
									</Button>
								) : (
									<Tooltip
										title={getScheduleButtonTooltip()}
										disableHoverListener={!isFormInvalid}
									>
										<span>
											<Button
												variant="outlined"
												onClick={handleGetScheduleClick}
												disabled={isFormInvalid}
											>
												Get Schedule
											</Button>
										</span>
									</Tooltip>
								)}
							</Box>
						</>
					)}
				</Grid>
			</Grid>
		</Paper>
	);
};

export default AdminTab;
